import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { useEditUserMutation } from 'api/usersApi'
import ErrorContextProvider, { useErrorContext } from 'errors/components/ErrorContextProvider'
import { t } from 'localization'
import { fullNameValidation } from 'shared/addressValidations'
import { Button } from 'shared/components/Button'
import SideSheet from 'shared/components/SideSheet'
import Spinner from 'shared/components/Spinner'
import { AccountQA, GlobalQA } from 'shared/dataAttributes'
import { useUser } from 'shared/hooks/useUser'
import { colors } from 'shared/lib'
import AccountPagePanel from './AccountPagePanel'
import EditAccount from './EditAccount'

const TP = 'account.components.accountPanel'

export const AccountPanel = () => {
  const [isEditing, setIsEditing] = useState(false)
  const { setErrors } = useErrorContext()
  const { currentUser } = useUser()
  const [editUser, { isLoading, isError, error }] = useEditUserMutation()

  const handleSubmit = async ({ fullName }: { fullName: string }) => {
    const userId = currentUser?.id
    const body = { id: userId, name: fullName }
    await editUser(body)
    setIsEditing(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
    setErrors([])
  }

  useEffect(() => {
    if (isError) {
      setErrors(error?.data?.localized_messages || error?.data?.messages)
    }
  }, [isError, error, setErrors])

  if (!currentUser || isLoading) {
    return (
      <AccountPagePanel qaAttr={AccountQA.Account} title={t(`${TP}.account`, 'Account')}>
        <Spinner showSpinner text={t(`${TP}.fetchingUserInfo`, 'Fetching User Info...')}>
          <Body />
        </Spinner>
      </AccountPagePanel>
    )
  }

  const { name, email } = currentUser

  return (
    <ErrorContextProvider>
      <Formik
        enableReinitialize
        initialValues={{ fullName: name }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object({ fullName: fullNameValidation() })}
      >
        {({ dirty, isValid, submitCount }) => (
          <>
            <AccountPagePanel
              qaAttr={AccountQA.Account}
              title={t(`${TP}.account`, 'Account')}
              isEditable
              onEdit={handleEdit}
            >
              <Body>
                <div data-qa={AccountQA.AccountFullname}>{name}</div>
                <Email data-qa={AccountQA.AccountEmail}>{email}</Email>
              </Body>
            </AccountPagePanel>
            <Form>
              <SideSheet.Responsive
                title={t(`${TP}.editAccount`, 'Edit Account')}
                isVisible={isEditing}
                onClose={() => setIsEditing(false)}
                isBackgroundOffWhiteMobile
              >
                <SideSheet.Body>
                  <EditAccount />
                </SideSheet.Body>
                <SideSheet.Footer>
                  <SaveChangesWrapper>
                    <Button
                      data-qa={GlobalQA.SideSheetSaveButton}
                      buttonType="primary2"
                      disabled={(submitCount > 0 && !isValid) || isLoading || !dirty}
                      $fill
                      type="submit"
                    >
                      {t(`${TP}.saveChanges`, 'Save Changes')}
                    </Button>
                  </SaveChangesWrapper>
                </SideSheet.Footer>
              </SideSheet.Responsive>
            </Form>
          </>
        )}
      </Formik>
    </ErrorContextProvider>
  )
}

const SaveChangesWrapper = styled.div`
  margin: 30px 20px;
`

const Body = styled.div`
  line-height: 18px;
  min-height: 40px;
`

const Email = styled.div`
  color: ${colors.FC2_GREY};
`
